/****************************************************
 * Module Name:  tallyLocationTransferReceive.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, Checkbox, Container, FormControlLabel, FormGroup, Tooltip, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import StoneDetailModal from "../common/stoneDetailModal";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { styled } from "@mui/material/styles";
import NoPermission from "../common/noPermission";

import StockTallyProxy from "../../utility/proxy/manager/stockTallyProxy";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import ChangeReceiveLocationProxy from "../../utility/proxy/manager/changeReceiveLocationProxy";
import ReceiveLocationModal from "./receiveLocationModal";
import { MdLocationOn } from "react-icons/md";
import CommonStoneSelection from "../common/commonStoneSelection";
import dayjs from "dayjs";
import PageTitle from "../common/pageTitle";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_REQUESTED": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#b6b9d6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        color: "#000",
        "&:hover": {
            backgroundColor: "#b6b9d6",
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--LOCATION_SENT": {
        backgroundColor: "#FEBD7D",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#FEBD7D",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

export default function TallyLocationTransferReceive() {

    const [stockData, setStockData] = useState([]);
    const [stockLoader, setStockLoader] = useState(false);
    const [loader, setLoader] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);

    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
    const [selectedTotalSize, setSelectedTotalSize] = useState("0");
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
    const [selectedavgPrice, setSelectedavgPrice] = useState("0");
    const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");
    const [sortModel, setSortModel] = React.useState([]);
    const [show, setShow] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);

    const [isCheckBoxSelected, setCheckBoxSelected] = useState(false);

    // eslint-disable-next-line
    const [stockTallyPermission, setStockTallyPermission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.OPERATION_STOCKTALLY
        )
    );

    useEffect(() => {
        PageTitle("OPERATION_STOCKTALLY_LOCATION_TRANSFER_RECEIVE");
    }, []);

    const handleSearch = () => {

        setStockLoader(true);
        let userLocationId = Constants.loggedInUser.locationid;

        if (isCheckBoxSelected) {
            let type = "LOCATION_TRANSFER";

            StockTallyProxy.GetAllStoneTransit(type, userLocationId, (response) => {
                if (response) {
                    const uniqueFilterValues = {
                        labshape: Utility.getDistinctOptions(response, 'labshape'),
                        distinctStatus: Utility.getDistinctOptions(response, 'status'),
                    }
                    prepareColumns(uniqueFilterValues);
                    setStockData(response);
                    setStockLoader(false);
                    if (selectionModel.length <= 0)
                        loadTotalData(response);
                } else {
                    setStockLoader(false);
                    setErrorSnackbar(true);
                }
            })
        } else {
            let type = 'TO_BE_RECEIVED';

            StockTallyProxy.GetAllLocationTransferStone(type, userLocationId, (response) => {
                if (response) {
                    const uniqueFilterValues = {
                        labshape: Utility.getDistinctOptions(response, 'labshape'),
                        distinctStatus: Utility.getDistinctOptions(response, 'status'),
                    }
                    prepareColumns(uniqueFilterValues);
                    setStockData(response);
                    setStockLoader(false);
                    if (selectionModel.length <= 0)
                        loadTotalData(response);
                } else {
                    setStockLoader(false);
                    setErrorSnackbar(true);
                }
            })
        }
    };

    const getAllStoneTransit = () => {
        setCheckBoxSelected(prevState => {
            const newState = !prevState;
            return newState;
        });
    };


    useEffect(() => {
        handleSearch();
    }, [isCheckBoxSelected]);

    const handleOpen = () => {
        setShow(true);
    };

    const handleClose = () => {
        setShowStoneDetailModal(false);
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handleValidation = (data) => {
        //TODO do validation if needed here
        return true;
    };

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, stockData, selectionModel);

        setStockData(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const changeLocation = (remark) => {
        setLoader(true);
        const selectionSet = new Set(selectionModel);
        const listdata = stockData
            .filter(({ stoneid }) => selectionSet.has(stoneid))
            .map(({ stoneid, consignmentno, consignmentdate }) => {

                return {
                    stoneid,
                    consignmentno,
                    consignmentdate: consignmentdate ? dayjs(consignmentdate).format('YYYY-MM-DD') : null,
                };
            });

        let data = {
            userid: Constants.loggedInUser.id,
            remark: remark,
            stones: selectionModel,
            consignmentdetails: listdata,
            locationid: Constants.loggedInUser.locationid,
        }

        ChangeReceiveLocationProxy.UpdateReceiveLocation(data, (responseData) => {
            if (responseData) {
                handleSearch();
                setLoader(false);
                setShow(false);
                setSuccessSnackbar(true);
            } else {
                setLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    const loadTotalData = (data) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size

        data.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

        });
        setNoOfSelectedStones(data.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    };

    useEffect(() => {
        if (selectionModel.length <= 0) {
            loadTotalData(stockData);
            return;
        }
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        stockData.forEach((iData) => {
            if (selectionModel.includes(iData.stoneid)) {
                let pricePerCarat = iData.price
                totalSize += iData.size;
                totalPrice += (iData.size * pricePerCarat);
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
            }
        });
        setNoOfSelectedStones(selectionModel.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    }, [selectionModel]);

    const prepareColumns = (uniqueFilterValues) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "status") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues.distinctStatus;
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);
                if (columnData.field === "stoneid") {
                    columnNames.push({
                        field: "suratid",
                        headerName: "SURATID",
                        headerAlign: "center",
                        align: "center",
                        width: 80,
                        renderCell: renderCellExpand,
                    })
                } else if (columnData.field === "shape") {
                    columnNames.push({
                        field: "labshape",
                        headerName: "LAB SHAPE",
                        headerAlign: "center",
                        align: "center",
                        width: 90,
                        renderCell: renderCellExpand,
                        type: "singleSelect",
                        valueOptions: uniqueFilterValues.labshape,
                    })
                } else if (columnData.field === "status") {
                    columnNames.push({
                        field: "consignmentno",
                        headerName: "CONSIGN. No",
                        headerAlign: "center",
                        align: "center",
                        width: 140,
                        type: "text",
                        renderCell: renderCellExpand,
                        cellClassName: (params) => 'lavenderCell',
                    });
                    columnNames.push({
                        field: "consignmentdate",
                        headerName: "CONSIGN. DATE",
                        headerAlign: "center",
                        align: "center",
                        width: 140,
                        valueGetter: (params) => {
                            if (params.value) {
                                return new Date(params.value);
                            }
                            return null;
                        },
                        renderCell: (params) => Utility.ConvertUtcToLocalDate(params.value),
                    });
                } else if (columnData.field === "total") {
                    columnNames.push({
                        field: "locationfrom",
                        headerName: "STONE LOCATION",
                        headerAlign: "center",
                        align: "center",
                        width: 135,
                        renderCell: renderCellExpand,
                    });
                    columnNames.push({
                        field: "senddate",
                        headerName: "SENT DATE",
                        headerAlign: "center",
                        align: "center",
                        width: 100,
                        type: "dateTime",
                        valueGetter: (params) => {
                            if (params.value) {
                                return new Date(params.value);
                            }
                            return null;
                        },
                        renderCell: (params) => Utility.ConvertUtcToLocalDate(params.value),
                    });
                    columnNames.push({
                        field: "locationto",
                        headerName: "SEND TO LOCATION",
                        headerAlign: "center",
                        align: "center",
                        width: 150,
                        renderCell: renderCellExpand,
                    });
                    columnNames.push({
                        field: "finaldestination",
                        headerName: "FINAL DESTINATION",
                        headerAlign: "center",
                        align: "center",
                        width: 140,
                        renderCell: renderCellExpand,
                    });
                    columnNames.push({
                        field: "export",
                        headerName: "EXPORT",
                        headerAlign: "center",
                        align: "center",
                        renderCell: (params) => params.value === true ? (
                            <CheckIcon color="success" />
                        ) : (
                            <ClearIcon color="error" />
                        ),
                    });
                }
            }
        });
        setColumnNames(columnNames);
    }

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            pinning: { position: 'left' },
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            cellClassName: 'stickyColumn',
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
            field: "boxid",
            headerName: "BOXID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            valueGetter: (params) => params.value === "LOCATION_SENT" ? "IN_TRANSIT" : params.value,
            renderCell: (params) => params.value === "LOCATION_SENT" ? "IN_TRANSIT" : params.value,
            cellClassName: (params) => `super-app-theme--${params.row.status.toUpperCase()}`,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "54fbd914-6ce7-44a8-9354-db6125ba1992": {
            field: "tag",
            headerName: "TAG",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "d4992de9-92ab-4f9e-ba12-6c746e935611": {
            field: "colordescriptions",
            headerName: "COLOR DESCRIPTIONS",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "564cee34-901f-4bf4-b7fc-f9f965771d8c": {
            field: "fancycolor1",
            headerName: "F.COL 1",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "7e1a0c81-4c41-4cda-bfff-8e2e81d75ce1": {
            field: "fancycolor2",
            headerName: "F.COL 2",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "ccba9873-174e-4060-963a-9893c3625bb2": {
            field: "fancycolorovertone",
            headerName: "OVERTONE",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
            field: "fancycolorintensity",
            headerName: "F.COL.INTENSITY",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "f3223657-c8d2-449c-b2f5-5a54c78401ce": {
            field: "tableinc",
            headerName: "TABLE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f114ad2c-8560-4da7-9b03-03ba6bfe39a5": {
            field: "sideinc",
            headerName: "SIDE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "e993a4f7-7c50-4d83-abae-fccc03382742": {
            field: "h_and_a",
            headerName: "H&A",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f2fe7666-6489-40ce-b4b7-040c68c88bf3": {
            field: "eyeclean",
            headerName: "EYE CLEAN",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "b38b9fdf-bfb8-43c6-82a2-1e366fa71523": {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "c95089ee-166d-4783-817c-2638f6d40df1": {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "2a6475ff-481f-48c6-9e92-3ede0b97d7c0": {
            field: "luster",
            headerName: "LUSTER",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "556ae429-3605-4df4-88d8-b5223fc2e2fb": {
            field: "tableblack",
            headerName: "TABLE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "5c70d1ca-2a7b-4660-8160-0beff7ad1150": {
            field: "sideblack",
            headerName: "SIDE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
    };


    const rows = stockData;
    const columns = columnNames;

    const allowed = Utility.isReadAllowed(stockTallyPermission);

    return (
        !allowed ? <NoPermission /> :
            <div>
                <Container maxWidth="xxl">
                    {showStoneDetailModal ? (
                        <StoneDetailModal
                            show={showStoneDetailModal}
                            stoneId={stoneId}
                            handleClose={handleClose}
                        />
                    ) : null}
                    <TabMenuSnackbar
                        show={successSnackbar}
                        message={'Success'}
                        variant={"success"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Success"}
                        handleClose={() => setSuccessSnackbar(false)}
                    />
                    <TabMenuSnackbar
                        show={errorSnackbar}
                        message={'Error'}
                        variant={"error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Error"}
                        handleClose={() => setErrorSnackbar(false)}
                    />
                    <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                m: "1px 15px 0 0",
                                color: "black",
                                padding: "8px 16px",
                                fontWeight: 'normal',
                                fontSize: Constants.defaultTableFontSize
                            }}
                        ><p>
                                <span>No of Stones: </span> <b>{noOfSelectedStones}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Weight: </span> <b>{selectedTotalSize}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Price: </span> <b>{selectedtotalPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Avg Price: </span> <b>{selectedavgPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Avg Discount: </span> <b>{selectedAvgDiscount}</b></p>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: '69vh'
                        }}
                    >
                        <StyledDataGrid
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{ height: "69vh", fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                            slots={{
                                toolbar: GridToolbar,
                            }}
                            rows={rows}
                            style={{}}
                            sortModel={sortModel}
                            onSortModelChange={(newModel) => setSortModel(newModel)}
                            density="compact"
                            columns={columns}
                            getRowId={(row) => row.stoneid}
                            checkboxSelection={true}
                            onRowSelectionModelChange={(newSelectionModel) => {
                                setSelectionModel(newSelectionModel);
                            }}
                            loading={stockLoader}
                            rowSelectionModel={selectionModel}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        kapanid: false,
                                        country: false,
                                        quality: false,
                                        shade: false,
                                        repacode: false,
                                        createdat: false,
                                        height: false,
                                        width: false,
                                        packetid: false,
                                        crownheight: false,
                                        crownangle: false,
                                        culetsize: false,
                                        culetcondition: false,
                                        depthpercent: false,
                                        gridlemin: false,
                                        intensitylongtitle: false,
                                        laserinscription: false,
                                        note: false,
                                        paviliondepth: false,

                                        gridlecondition: false,
                                        gridlepercent: false,
                                        pavilionangle: false,
                                        white: false,

                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />

                        <Box sx={{
                            position: "absolute",
                            left: "0.4rem",
                            top: "1px"
                        }}>
                            <CommonStoneSelection title={"Enter multiple Stone Ids"} handleSelection={handleSelection} bgColor='#454545' buttonDisabled={rows.length === 0} />
                        </Box>

                        <Box
                            style={{
                                position: "absolute",
                                top: 4,
                                left: "455px",
                                display: "flex",
                                alignItems: "center"
                            }}
                        >
                            <Box sx={{
                                height: "39px",
                                "& .MuiFormControlLabel-root": {
                                    margin: "0px 0px",
                                }
                            }}>
                                <FormGroup>
                                    <FormControlLabel control={<Checkbox onChange={getAllStoneTransit} size="small" />} />
                                </FormGroup>
                            </Box>
                            <Box >
                                <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>Location Transfer Transit</Typography>
                            </Box>
                        </Box>
                        <Box
                            style={{
                                position: "absolute",
                                top: 6,
                                right: "265px",
                                display: "flex",
                            }}
                        >
                            {stockLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch()}
                                        color="primary"
                                        fontSize="large"
                                        sx={{ cursor: "pointer", height: "1.8rem", width: "1.8rem" }}
                                    />
                                </Tooltip>
                            )}
                            {Utility.isAddAllowed(stockTallyPermission) ? (
                                <Tooltip title={"Change Location"}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        component="label"
                                        sx={{ ml: "15px", mr: "10px" }}
                                        color="success"
                                        startIcon={<MdLocationOn />}
                                        disabled={selectionModel.length < 1 || isCheckBoxSelected || stockLoader}
                                        onClick={handleOpen}
                                    >
                                        MARK AS RECEIVED
                                    </Button>
                                </Tooltip>
                            ) : null}
                            {show && (
                                <ReceiveLocationModal
                                    show={true}
                                    setShow={setShow}
                                    handleValidation={handleValidation}
                                    handleSave={changeLocation}
                                    stoneId={selectionModel}
                                    sourceData={stockData}
                                    loading={loader}
                                />
                            )}
                        </Box>
                    </Box>
                </Container>
            </div>
    );
}
